import React, { FC, useCallback, useContext, useState } from 'react';

import { IBaseProps } from '@rbi-ctg/frontend';
import { MenuObject } from '@rbi-ctg/menu';
import { MenuObjectTypes } from 'enums/menu';
import {
  Channel,
  ICaloriesFragment,
  IPricingAndAvailabilityFragment,
  IStoreMenuQuery,
  PosDataServiceMode,
  ServiceMode,
  useStoreMenuLazyQuery,
} from 'generated/graphql-gateway';
import {
  IGetComboWithVendorConfigQuery,
  IGetItemWithVendorConfigQuery,
  IGetMenuSectionsQuery,
  IGetPickerQuery,
  useGetComboWithVendorConfigLazyQuery,
  useGetItemWithVendorConfigLazyQuery,
  useGetMenuSectionsLazyQuery,
  useGetPickerWithVendorConfigLazyQuery,
} from 'generated/sanity-graphql';
import { withLazySimpleCache } from 'state/graphql/with-simple-cache';
import { useServiceModeContext } from 'state/service-mode';
import logger from 'utils/logger';
import { useMemoAll } from 'utils/use-memo-all';

import { IUseDayPart } from './types';
import { useDayPartSelector } from './use-day-part-selector';

export interface IMainMenuContext extends IUseDayPart {
  clearStoreMenuCache: () => void;
  getStoreMenu: (region: string, storeId: string) => void;
  getMenuObject(id: string): IMenuObjectState;
  getMainMenu(menuId?: string, region?: string, storeId?: string | null): IState;
  getPricingAndAvailability: (id: string) => IPricingAndAvailabilityFragment | null | undefined;
  getCalories: (item: MenuObject) => ICaloriesFragment['calories'] | null;
  data: IGetMenuSectionsQuery | undefined;
  loading: boolean;
  hasError: boolean;
  getSection: (id: string) => Promise<void>;
  getPickerDetail: (id: string) => Promise<void>;
  getItemDetail: (id: string) => Promise<void>;
  getComboDetail: (id: string) => Promise<void>;
  pickerLoading: boolean;
  pickerData: IGetPickerQuery | undefined;
  comboLoading: boolean;
  comboData: IGetComboWithVendorConfigQuery | undefined;
  itemLoading: boolean;
  itemData: IGetItemWithVendorConfigQuery | undefined;
  storeMenuLoading: boolean;
  storeMenuCalled: boolean;
  storeMenuData: IStoreMenuQuery | undefined;
  setIsOffer: (isOffer: boolean) => void;
  isOffer: boolean;
}

export interface IMenuObjectState {
  data: MenuObject | null;
  loading: boolean;
}
interface IState {
  data: IGetMenuSectionsQuery['Menu'] | undefined;
  loading: boolean;
}

export const MainMenuContext = React.createContext<IMainMenuContext>({
  clearStoreMenuCache: () => null,
  data: undefined,
  loading: false,
  getStoreMenu: () => null,
  getMenuObject: () => ({ data: {} as MenuObject, loading: false }),
  getMainMenu: () => ({ data: null, loading: false }),
  getDayPart: () => ({ currentDayPart: null, dayParts: [] }),
  setDayPart: () => null,
  breakfastInterval: undefined,
  mainMenuInterval: undefined,
  getPricingAndAvailability: () => null,
  getCalories: () => null,
  hasError: false,
  getSection: () => Promise.resolve(),
  getPickerDetail: () => Promise.resolve(),
  getItemDetail: () => Promise.resolve(),
  getComboDetail: () => Promise.resolve(),
  pickerLoading: false,
  pickerData: undefined,
  comboLoading: false,
  comboData: undefined,
  itemLoading: false,
  itemData: undefined,
  storeMenuLoading: false,
  storeMenuCalled: false,
  storeMenuData: undefined,
  setIsOffer: isOffer => isOffer,
  isOffer: false,
});

const matchSanityIdFromPath = (id: string) => {
  const [, sanityId] = id.split(/-(.*)/);

  if (!sanityId) {
    return '';
  }

  return sanityId;
};

const useCachedGetMenuSectionsLazyQuery = withLazySimpleCache(useGetMenuSectionsLazyQuery);
const useCachedStoreMenuLazyQuery = withLazySimpleCache(useStoreMenuLazyQuery);

export const MainMenuProvider: FC<React.PropsWithChildren<IBaseProps>> = ({ children }) => {
  const { setDayPart, getDayPart, breakfastInterval, mainMenuInterval } = useDayPartSelector();
  const { serviceMode } = useServiceModeContext();

  const [menuSectionMap, setMenuSectionMap] = useState<Map<string, any>>(new Map());
  const [getMenuSectionsLazy, { loading, error, data }] = useCachedGetMenuSectionsLazyQuery({
    onCompleted(data) {
      setMenuSectionMap(new Map(data.Menu?.options?.map(x => [(x as any)._id, x])));
    },
  });
  const [
    getPicker,
    { loading: pickerLoading, error: pickerError, data: pickerData },
  ] = useGetPickerWithVendorConfigLazyQuery();
  const [
    getCombo,
    { loading: comboLoading, error: comboError, data: comboData },
  ] = useGetComboWithVendorConfigLazyQuery();
  const [
    getItem,
    { loading: itemLoading, error: itemError, data: itemData },
  ] = useGetItemWithVendorConfigLazyQuery();

  const [storeMenuDataMap, setStoreMenuDataMap] = useState<
    Map<
      string,
      {
        readonly __typename?: 'Entity' | undefined;
      } & IPricingAndAvailabilityFragment &
        ICaloriesFragment
    >
  >(new Map());

  const [
    getStoreMenuLazy,
    {
      loading: storeMenuLoading,
      error: storeMenuError,
      called: storeMenuCalled,
      data: storeMenuData,
    },
  ] = useCachedStoreMenuLazyQuery({
    onCompleted(data) {
      setStoreMenuDataMap(new Map(data.storeMenu?.map(menu => [menu.id, menu])));
    },
  });

  const getStoreMenu = useCallback(
    (region: string, storeId: string) =>
      getStoreMenuLazy({
        allowFromCache: true,
        variables: {
          channel: Channel.WHITELABEL,
          region,
          storeId,
          serviceMode:
            ServiceMode.DELIVERY === serviceMode
              ? PosDataServiceMode.DELIVERY
              : PosDataServiceMode.PICKUP,
        },
      }),
    [getStoreMenuLazy, serviceMode]
  );
  const [isOffer, setIsOffer] = useState(false);

  const clearStoreMenuCache = useCallback(() => {
    setStoreMenuDataMap(new Map());
  }, []);

  const getMainMenu = useCallback(
    (menuId?: string, region?: string, storeId?: string | null) => {
      if (menuId && !storeId) {
        getMenuSectionsLazy({ allowFromCache: true, variables: { id: menuId } });
      }

      if (menuId && storeId && region) {
        // this should be awaited but it really screws up the types and it works anyways
        Promise.all([
          getStoreMenu(region, storeId),
          getMenuSectionsLazy({ allowFromCache: true, variables: { id: menuId } }),
        ]);
      }

      return {
        loading,
        data: data?.Menu,
      };
    },
    [data, getMenuSectionsLazy, getStoreMenu, loading]
  );
  const getPickerDetail = useCallback(
    async (id: string) => {
      const sanityId = matchSanityIdFromPath(id);

      await getPicker({ variables: { id: sanityId } });
    },
    [getPicker]
  );
  const getComboDetail = useCallback(
    async (id: string) => {
      const sanityId = matchSanityIdFromPath(id);

      await getCombo({ variables: { id: sanityId } });
    },
    [getCombo]
  );
  const getItemDetail = useCallback(
    async (id: string) => {
      const sanityId = matchSanityIdFromPath(id);

      await getItem({ variables: { id: sanityId } });
    },
    [getItem]
  );

  const getCachedSection = useCallback(
    (id: string) => {
      return menuSectionMap.get(id.replace('Section:', ''));
    },
    [menuSectionMap]
  );

  const getPricingAndAvailability = useCallback(
    (id: string) => {
      if (storeMenuLoading || !storeMenuData) {
        return null;
      }

      return storeMenuDataMap.get(id);
    },
    [storeMenuData, storeMenuDataMap, storeMenuLoading]
  );

  const getCalories = useCallback(
    (item: MenuObject) => {
      if (!item || !item._id) {
        return null;
      }

      return storeMenuDataMap.get(item._id)?.calories || null;
    },
    [storeMenuDataMap]
  );

  // TODO - the switch here is likely legacy and can be removed
  const getMenuObject = useCallback(
    (id: string) => {
      const [type, sanityId] = id.split(/-(.*)/);

      switch (type) {
        case MenuObjectTypes.SECTION:
          return { loading: false, data: getCachedSection(`Section:${sanityId}`) as MenuObject };
        default:
          logger.error(`${id} is unhandled by getMenuObject - may cause a user facing issue`);
          return { loading: false, data: {} as MenuObject };
      }
    },
    [getCachedSection]
  );

  const hasError = Boolean(error || pickerError || comboError || itemError || storeMenuError);

  const value = useMemoAll({
    clearStoreMenuCache,
    data,
    getStoreMenu,
    getMainMenu,
    getMenuObject,
    getPickerDetail,
    getComboDetail,
    getItemDetail,
    getSection: getCachedSection,
    hasError,
    loading,
    pickerLoading,
    pickerData,
    comboLoading,
    comboData,
    itemLoading,
    itemData,
    storeMenuCalled,
    storeMenuLoading,
    storeMenuData,
    setDayPart,
    getDayPart,
    breakfastInterval,
    mainMenuInterval,
    getPricingAndAvailability,
    getCalories,
    isOffer,
    setIsOffer,
  });

  return <MainMenuContext.Provider value={value}>{children}</MainMenuContext.Provider>;
};

// MainMenuContext.Provider.whyDidYouRender = true;

export const useMainMenuContext = () => useContext(MainMenuContext);
export default MainMenuContext.Consumer;
